import type { ReactNode } from 'react';

import { useTypedAppFormContext } from '@/components/Form';

export type AudienceRuleFormValues = {
  name: string;
  description: string;
  rules: RuleBuilderRulesType;
  version: string;
};

export function useAudienceRuleForm() {
  return useTypedAppFormContext({ defaultValues: {} as AudienceRuleFormValues });
}

export type RuleBuilderType = {
  id?: string;
  name?: string;
  description?: string;
  rules: RuleBuilderRulesType;
};

export type RuleBuilderRulesType = {
  dateRange: {
    unit: 'day'; // 'month' or 'year' in the future?
    count: number;
  };
  orConditions: {
    andConditions: RuleBuilderConditionType[];
  }[];
};

export type OrConditionPath = `rules.orConditions[${number}]`;
export type AndConditionsPath = `${OrConditionPath}.andConditions`;
export type AndConditionPath = `${AndConditionsPath}[${number}]`;
export type AttributesPath = `${AndConditionPath}.attributes`;
export type AttributePath = `${AttributesPath}[${number}]`;
export type AggregatePath = `${AndConditionPath}.aggregate`;

export type RuleBuilderDateRangeType = {
  unit: 'day'; // 'month' or 'year' in the future?
  count: number;
};

export type RuleBuilderObjectType = 'company' | 'event.visit' | 'event.impression' | 'event.intent'; // 'opportunity' in the future?

export type RuleBuilderConditionType = {
  object: RuleBuilderObjectType;
  cardinality?: 'many' | 'one'; // 'one' by default
  attributes: Partial<RuleBuilderAttributeType>[];
  aggregate?: RuleBuilderAggregateType; // only exists if cardinality is 'many'
};

export type ConditionFieldValues = {
  attributes: Partial<RuleBuilderAttributeType>[];
};

export type ConditionGroupFieldValues = {
  andConditions: RuleBuilderConditionType[];
};

export type RuleBuilderAttributeType = {
  type: 'string' | 'number' | 'boolean' | 'temporal';
  display: 'picklist' | 'autocomplete' | 'text' | 'picker';
  field: string;
  operator: AttributeOperatorType;
  value: AttributeValueType;
};

export type AggregateFunctionType = 'count'; // could have 'average', 'min', 'max' later...

export type RuleBuilderAggregateType = {
  type: 'number';
  function: AggregateFunctionType;
  field?: string; // only exists if function is not 'count'
  operator: NumberAttributeOperatorType;
  value: number[];
};

export type AggregateFieldValues = Pick<RuleBuilderAggregateType, 'operator' | 'value'>;

export type AttributeValueType = string[] | number[] | boolean[];

export type AttributeFieldValues = {
  operator?: AttributeOperatorType;
  value?: AttributeValueType;
};

/** OPERATOR TYPES **/
export type StringAttributeOperatorType = 'eq' | 'ne' | 'contains' | 'notContains'; // eventually could add 'startsWith', 'endsWith', etc
export type NumberAttributeOperatorType = 'eq' | 'ne' | 'gt' | 'gte' | 'lt' | 'lte';
export type BooleanAttributeOperatorType = 'eq' | 'ne';
export type TemporalAttributeOperatorType = 'rollingTimeInterval'; // possibly 'absolute' for fixed dates

export type AttributeOperatorType =
  | StringAttributeOperatorType
  | NumberAttributeOperatorType
  | BooleanAttributeOperatorType
  | TemporalAttributeOperatorType;

export type RuleEntityAttributeType = {
  id: string;
  type: 'string' | 'number' | 'boolean' | 'temporal';
  display: 'picklist' | 'autocomplete' | 'text' | 'picker';
  name: string;
  icon?: ReactNode;
  isDefault?: boolean; // if true, this attribute is automatically added when the rule is created
  isRequired?: boolean; // if true, the remove button is hidden for this attribute
};

export type RuleEntityType =
  | {
      id: 'company';
      name: string;
      icon: ReactNode;
      cardinality: 'one';
      attributes: RuleEntityAttributeType[];
    }
  | {
      id: 'event.visit' | 'event.impression' | 'event.intent';
      name: string;
      icon: ReactNode;
      cardinality: 'many';
      hidden?: boolean;
      hideAggregate?: boolean;
      attributes: RuleEntityAttributeType[];
    };
